import React, { useEffect, useState } from "react";
import { api, fmtError, fmtDate } from "@/lib/apiClient";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import CurrencyPicker from "@/components/CurrencyPicker";
import { Plus } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Clients() {
    const { user } = useAuth();
    const [list, setList] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", industry: "", primary_contact_name: "", primary_contact_email: "", currency: "USD", notes: "" });

    const load = async () => {
        try {
            const { data } = await api.get("/clients");
            setList(data);
        } catch (e) {}
    };
    useEffect(() => { load(); }, []);

    const canCreate = user?.role === "client_servicing" || user?.role === "super_admin";

    const submit = async (e) => {
        e.preventDefault();
        try {
            await api.post("/clients", form);
            toast.success("Client added");
            setOpen(false);
            setForm({ name: "", industry: "", primary_contact_name: "", primary_contact_email: "", currency: "USD", notes: "" });
            load();
        } catch (e) { toast.error(fmtError(e.response?.data?.detail) || e.message); }
    };

    return (
        <div className="space-y-6">
            <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <div className="overline mb-1">// Directory</div>
                    <h1 className="font-display text-4xl tracking-tighter">Client Organizations</h1>
                    <p className="text-slate-500 mt-2 text-sm">{list.length} clients across the tenant.</p>
                </div>
                {canCreate && (
                    <Button onClick={() => setOpen(true)} data-testid="add-client-button" className="rounded-none bg-slate-900 hover:bg-slate-800 text-white gap-2">
                        <Plus size={14} weight="bold" /> Add Client
                    </Button>
                )}
            </header>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
                {list.map((c) => (
                    <div key={c.id} className="bg-white p-5" data-testid={`client-card-${c.id}`}>
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="overline mb-1">{c.industry || "—"}</div>
                                <h3 className="font-display text-lg">{c.name}</h3>
                            </div>
                            {c.currency && (
                                <span className="font-mono text-[10px] uppercase tracking-wider bg-slate-100 text-slate-700 px-2 py-0.5">
                                    {c.currency}
                                </span>
                            )}
                        </div>
                        <div className="text-xs font-mono text-slate-500 mt-3 space-y-0.5">
                            <div>{c.primary_contact_name || "—"}</div>
                            <div className="lowercase">{c.primary_contact_email || "—"}</div>
                        </div>
                        <div className="mt-4 pt-3 border-t border-slate-100 overline text-slate-400">added {fmtDate(c.created_at)}</div>
                    </div>
                ))}
                {list.length === 0 && (
                    <div className="bg-white p-12 text-center text-sm text-slate-500 md:col-span-3">No clients yet.</div>
                )}
            </div>

            {open && (
                <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-start justify-center p-4" data-testid="add-client-modal">
                    <form onSubmit={submit} className="bg-white border border-slate-200 max-w-lg w-full mt-12">
                        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                            <h3 className="font-display text-lg">New Client</h3>
                            <button type="button" onClick={() => setOpen(false)} className="text-2xl leading-none text-slate-500">×</button>
                        </div>
                        <div className="p-6 space-y-4">
                            <Field label="Name"><Input data-testid="client-form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none border-slate-300" /></Field>
                            <Field label="Industry"><Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} className="rounded-none border-slate-300" /></Field>
                            <Field label="Primary contact name"><Input value={form.primary_contact_name} onChange={(e) => setForm({ ...form, primary_contact_name: e.target.value })} className="rounded-none border-slate-300" /></Field>
                            <Field label="Primary contact email"><Input type="email" value={form.primary_contact_email} onChange={(e) => setForm({ ...form, primary_contact_email: e.target.value })} className="rounded-none border-slate-300" /></Field>
                            <Field label="Default currency">
                                <CurrencyPicker
                                    value={form.currency}
                                    onChange={(v) => setForm({ ...form, currency: v })}
                                    testid="client-form-currency"
                                />
                            </Field>
                            <Field label="Notes"><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="rounded-none border-slate-300" /></Field>
                        </div>
                        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
                            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none border-slate-300">Cancel</Button>
                            <Button type="submit" data-testid="client-form-submit" className="rounded-none bg-slate-900 hover:bg-slate-800 text-white">Add client</Button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <Label className="overline mb-1.5 block">{label}</Label>
            {children}
        </div>
    );
}
